import EmptyState from 'components/EmptyState';
import { Button } from 'components/ui/button';
import { Dialog, DialogClose, DialogContent, DialogFooter, DialogHeader, DialogTitle } from 'components/ui/dialog';
import { Skeleton } from 'components/ui/skeleton';
import { FOCUS_RING_INSET } from 'layout/NewDesignLayout/focusRing';
import { useId, useState } from 'react';
import rehypeSanitize from 'rehype-sanitize';
import { useGetTimecardDescriptionRulesQuery } from 'store/api/timecards';
import { Streamdown } from 'streamdown';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';

/**
 * The rules' markdown as Streamdown draws it, restyled: the set's heading, its groups in the record's field-group
 * style, and each rule with no bullet and a hanging indent, its bold code in a column of its own.
 */
const RULES_PROSE_CLASS = cn(
    'markdown-prose text-sm leading-relaxed',
    '[&_h2]:mt-0 [&_h2]:mb-1 [&_h2]:text-base [&_h2]:font-semibold',
    '[&_h3]:text-label [&_h3]:text-grey-600 new:[&_h3]:text-muted-foreground [&_h3]:mt-5 [&_h3]:mb-2 [&_h3]:font-semibold [&_h3]:uppercase',
    '[&_p]:mt-0 [&_p]:mb-3 [&_ul]:my-0 [&_ul]:list-none [&_ul]:ps-0',
    '[&_li]:my-1.5 [&_li]:py-0 [&_li]:ps-10 [&_li]:-indent-10 [&_li>p]:my-0',
    '[&_li>[data-streamdown=strong]:first-child]:inline-block [&_li>[data-streamdown=strong]:first-child]:w-9 [&_li>[data-streamdown=strong]:first-child]:indent-0'
);

/** Stored text, rendered as the help centre renders its pages: no raw HTML, and sanitized. */
function RulesMarkdown({ markdown }: { markdown: string }) {
    return (
        <div className={RULES_PROSE_CLASS}>
            <Streamdown mode="static" controls={false} skipHtml rehypePlugins={[rehypeSanitize]}>
                {markdown}
            </Streamdown>
        </div>
    );
}

const ERROR_TITLE_ID = 'timecards.form.description-rules-error';

type Props = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
};

/** The timecard description rules as stored: the generic ones, then the firm's own under their own heading. */
export function DescriptionRulesDialog({ open, onOpenChange }: Props) {
    const titleId = useId();
    // Read once the dialog is first opened, and kept through its close animation.
    const [isWanted, setIsWanted] = useState(open);
    if (open && !isWanted) {
        setIsWanted(true);
    }
    const { data: rules, isError, refetch } = useGetTimecardDescriptionRulesQuery(undefined, { skip: !isWanted });

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="flex max-h-[calc(100dvh-2rem)] flex-col gap-0 overflow-hidden p-0 sm:max-w-[640px]">
                <DialogHeader className="border-border shrink-0 border-b py-4 pr-14 pl-6">
                    <DialogTitle id={titleId} className="text-base font-semibold">
                        {t('timecards.form.description-rules-title')}
                    </DialogTitle>
                </DialogHeader>
                {/* A named, focusable scroller: the keyboard can scroll the rules where the browser does not focus
                    a scroller itself. */}
                <div
                    role="region"
                    aria-labelledby={titleId}
                    tabIndex={0}
                    className={cn('min-h-0 overflow-y-auto overscroll-contain px-6 py-5', FOCUS_RING_INSET)}
                >
                    {/* What the read ended in, said by a region mounted before it ends. */}
                    <p role="status" className="sr-only">
                        {isError && !rules ? t(ERROR_TITLE_ID) : ''}
                    </p>
                    {rules ? (
                        <>
                            <RulesMarkdown markdown={rules.generic} />
                            {rules.firm && (
                                <div className="border-border mt-6 border-t pt-5">
                                    <RulesMarkdown markdown={rules.firm} />
                                </div>
                            )}
                        </>
                    ) : isError ? (
                        <EmptyState
                            size="panel"
                            variant="error"
                            errorTitleId={ERROR_TITLE_ID}
                            onRetry={refetch}
                            surface="timecard-description-rules"
                        />
                    ) : (
                        <div className="flex flex-col gap-2" aria-busy="true">
                            <Skeleton className="h-4 w-1/2" />
                            <Skeleton className="h-3.5 w-full" />
                            <Skeleton className="h-3.5 w-11/12" />
                        </div>
                    )}
                </div>
                <DialogFooter className="border-border shrink-0 border-t px-6 py-3">
                    <DialogClose render={<Button variant="outline" />}>{t('close')}</DialogClose>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
